import config from "appConfig";

import { getLinkForCourseLesson } from "../../../../services/Utils";
import lesson0203 from "../lesson-03/lesson";
import lesson0204 from "../lesson-04/lesson";
import lesson0214 from "../lesson-14/lesson";

const linkLesson0202 = getLinkForCourseLesson(
  "4eb31243-b6cb-423f-919d-15bba103f7ce",
  "e61b40c7-1c44-4ca9-ab1b-ea66cefd9b90"
);
const linkLesson0203 = getLinkForCourseLesson("4eb31243-b6cb-423f-919d-15bba103f7ce", lesson0203.id);
const linkLesson0204 = getLinkForCourseLesson("4eb31243-b6cb-423f-919d-15bba103f7ce", lesson0204.id);
const linkLesson0214 = getLinkForCourseLesson("4eb31243-b6cb-423f-919d-15bba103f7ce", lesson0214.id);

const theory = `<p>Loudness is a psychoacoustic parameter representing the perceived level or intensity of a sound [1]. This parameter is extremely useful, as sounds of equal measured Sound Pressure Level (SPL) but different spectral components may elicit different loudness sensations. For example, a 60 dB pure tone of 1 kHz will have different loudness in comparison to a 60 dB pure tone of 100 Hz [2]. A perception-based description of the intensity of an auditory event therefore requires the usage of the psychoacoustics parameter loudness.<br class=newline>
<p>
   The estimation of loudness values not only considers the sensitivity of human sound perception to different frequencies (i.e. spectral weighting) but also the interactions (e.g., masking) between different signal components resulting from human signal processing based on auditory filters, known as critical bands [3, 4] (discussed in <a href="${linkLesson0203}" target="_blank">Lesson 2.3</a>). This means that the perceived doubling of a sound’s level will correspond to a doubling of the loudness value.<br class=newline>
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/02/07/theory/figure-1.svg alt="Figure 1. Equal loudness contours (ELC) [2]."><figcaption class=caption>Figure 1. Equal loudness contours (ELC) [2].</figcaption></figure>
<p>
   <a href="#" data-img-src=${config.ASSETS_URL}/02/07/theory/figure-1.svg data-img-alt="Figure 1. Equal loudness contours (ELC) [2].">Figure 1</a> shows the relationship between frequency and perceived loudness for different SPLs. Each contour (solid line) in the figure connects the points in the graph that elicit the same loudness sensation and are therefore denoted as <span class=cmti-10>isophones</span>. For pure tones, the <span class=cmti-10>loudness </span><span class=cmti-10>level </span>(
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>L</mi>
         </mrow>
         <mrow>
            <mi>N</mi>
         </mrow>
      </msub>
   </math>
   ) can be expressed with the unit phons, which corresponds to the SPL value of a 1 kHz sinusoid with the same perceived intensity. For example, the loudness level for a sinusoid of 1 kHz at 60 dB and a sinusoid of 100 Hz at 78.6 dB equals
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>L</mi>
         </mrow>
         <mrow>
            <mi>N</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mstyle class=text>
         <mtext>60</mtext>
      </mstyle>
      <mstyle class=text>
         <mtext>phons</mtext>
      </mstyle>
   </math>
   in both cases. Loudness is generally computed as the integral across <span class=cmti-10>specific loudness</span> (
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msup>
         <mrow>
            <mi>N</mi>
         </mrow>
         <mrow>
            <mi>′</mi>
         </mrow>
      </msup>
   </math>
   ) values (in
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mstyle class=text>
         <mtext>sone</mtext>
      </mstyle>
      <mi> </mi>
      <mo class=MathClass-bin>∕</mo>
      <mi> </mi>
      <mstyle class=text>
         <mtext>bark</mtext>
      </mstyle>
   </math>
   ) estimated for each band of the auditory filter bank (i.e. critical band).<br class=newline>
<p>
   The unit used to measure loudness (
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>N</mi>
   </math>
   ) is sone. One sone is defined as the loudness of a 40 dB 1 kHz pure tone (equivalent to
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>L</mi>
         </mrow>
         <mrow>
            <mi>N</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mstyle class=text>
         <mtext>40</mtext>
      </mstyle>
      <mstyle class=text>
         <mtext>phons</mtext>
      </mstyle>
   </math>
   ). As mentioned above, this transformation into a linear scale means that a doubling in the perceived intensity of a sound corresponds to a doubling in the loudness (
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>N</mi>
   </math>
   ) value and an increase of 10 phons. This relation is illustrated in <a href="#" data-img-src=${config.ASSETS_URL}/02/07/theory/table-1.svg data-img-alt="Table 1.
   Relationship between SPL, loudness level (
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>L</mi>
         </mrow>
         <mrow>
            <mi>N</mi>
         </mrow>
      </msub>
   </math>
   ) and loudness (
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>N</mi>
   </math>
   ) for a 1 kHz sinusoid.">Table 1</a>.
   <figure class=float>
      <figcaption class=caption>Table 1.
            Relationship between SPL, loudness level (
            <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
               <msub>
                  <mrow>
                     <mi>L</mi>
                  </mrow>
                  <mrow>
                     <mi>N</mi>
                  </mrow>
               </msub>
            </math>
            ) and loudness (
            <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
               <mi>N</mi>
            </math>
            ) for a 1 kHz sinusoid.
      </figcaption>
      <img src=${config.ASSETS_URL}/02/07/theory/table-1.svg alt="Table 1.
      Relationship between SPL, loudness level (
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <msub>
            <mrow>
               <mi>L</mi>
            </mrow>
            <mrow>
               <mi>N</mi>
            </mrow>
         </msub>
      </math>
      ) and loudness (
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <mi>N</mi>
      </math>
      ) for a 1 kHz sinusoid.">
   </figure>
<p>From <a href="#" data-img-src=${config.ASSETS_URL}/02/07/theory/figure-1.svg data-img-alt="Figure 1. Equal loudness contours (ELC) [2].">Figure 1</a> it can be seen that lower frequency sounds require a higher SPL to elicit the same loudness perception as higher frequency sounds. It can also be seen that in the lower frequency region, the difference between dB and phon values is more pronounced than in higher frequency ranges. The contours also form a dip around 4 kHz where our ears are most sensitive (see <a href="${linkLesson0202}" target="_blank">Lesson 2.2</a>), thus requiring a lower SPL to perceive sounds.<br class=newline>
<p>The loudness of a sound depends on the following factors:

<p><b>frequency:</b> sinusoids with same SPL but different frequency are not perceived with equal loudness by the human auditory system
   <p><b>spectral contribution:</b> broadband signals are perceived as louder than narrow band signals with an equal sound pressure level
   <p><b>sound pressure level:</b> the perception of loudness depends on the intensity of the stimulus. An increase by 10 dB (factor of 3) will translate into to a doubling of loudness
   <p><b>simultaneous masking:</b> as described in <a href="${linkLesson0204}" target="_blank">Lesson 2.4</a>, the perception of individual frequency components can be affected by the intensity of surrounding sound components (e.g., tones, narrow-band noise, etc.), especially within a critical band. This interaction is known as masking. In the presence of a prominent (high SPL) component, higher frequency components will be perceived as quieter (less loud)
   <p><b>stimulus duration:</b> Above a duration of 1000 ms the perceived loudness remains constant. However, for duration less than that, loudness reduces with reducing duration  [1]
<h4 class=likesubsectionHead>Relation to sensory pleasantness and sound quality</h4>
<p>Experiments have shown that sensory pleasantness is reduced with increasing loudness [1]. Generally speaking, this means that quieter sounds are usually perceived as more pleasant than louder sounds. Based on this and similar research results, loudness analysis has become particularly useful in applications such as:
   <p><b>product sound evaluation:</b> for many years the automotive industry has spent significant effort into measuring and improving the sound quality of vehicle noise. Loudness is among the main parameters used to determine sound quality of vehicles. Similarly, loudness is also a major contributor to the sound quality of household appliances, such as vacuum cleaners or refrigerators.
   <p><b>environmental sound evaluation:</b> extended exposure to loud sources of noise is known to be detrimental for human health. For this reason, loudness is an important parameter for a perception-based analysis of environmental sounds.
   <p><b>annoyance:</b> loudness is an important contributor when determining psychoacoustic annoyance. A decrease in sensory pleasantness has been observed with increasing loudness beginning with 20 sone.Below this value sensory pleasantness is not affected by loudness  1].

<h4 class=likesubsectionHead>Standards</h4>
<p>
   The two commonly used standards for loudness calculation are DIN 45631/A1 [5] and ISO 532-1 [6]. The DIN 45631/A1 standard was published in 2010. It uses a digital filter bank to create a 1/3 octave spectrum. This filter bank models human auditory processing in the inner ear, where spectral components located in close proximity of each other are processed inside separate frequency bands. The specific loudness (
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msup>
         <mrow>
            <mi>N</mi>
         </mrow>
         <mrow>
            <mi>′</mi>
         </mrow>
      </msup>
   </math>
   ) is calculated individually for each frequency band [1] as
<table class=equation>
   <tr>
      <td style="font-size: 10px;">
         <math display=block xmlns=http://www.w3.org/1998/Math/MathML class=equation>
            <msup>
               <mrow>
                  <mi>N</mi>
               </mrow>
               <mrow>
                  <mi>′</mi>
               </mrow>
            </msup>
            <mo class=MathClass-rel>=</mo>
            <mn>0</mn>
            <mo class=MathClass-punc>.</mo>
            <mn>08</mn>
            <msup>
               <mrow>
                  <mrow>
                     <mo form=prefix fence=true>(</mo>
                     <mrow>
                        <mfrac>
                           <mrow>
                              <msub>
                                 <mrow>
                                    <mi>E</mi>
                                 </mrow>
                                 <mrow>
                                    <mi mathvariant=italic>TQ</mi>
                                 </mrow>
                              </msub>
                           </mrow>
                           <mrow>
                              <msub>
                                 <mrow>
                                    <mi>E</mi>
                                 </mrow>
                                 <mrow>
                                    <mn>0</mn>
                                 </mrow>
                              </msub>
                           </mrow>
                        </mfrac>
                     </mrow>
                     <mo form=postfix fence=true>)</mo>
                  </mrow>
               </mrow>
               <mrow>
                  <mn>0</mn>
                  <mo class=MathClass-punc>.</mo>
                  <mn>23</mn>
               </mrow>
            </msup>
            <mrow>
               <mo form=prefix fence=true>[</mo>
               <mrow>
                  <msup>
                     <mrow>
                        <mrow>
                           <mo form=prefix fence=true>(</mo>
                           <mrow>
                              <mn>0</mn>
                              <mo class=MathClass-punc>.</mo>
                              <mn>5</mn>
                              <mo class=MathClass-bin>+</mo>
                              <mn>0</mn>
                              <mo class=MathClass-punc>.</mo>
                              <mn>5</mn>
                              <mfrac>
                                 <mrow>
                                    <mi>E</mi>
                                 </mrow>
                                 <mrow>
                                    <msub>
                                       <mrow>
                                          <mi>E</mi>
                                       </mrow>
                                       <mrow>
                                          <mi mathvariant=italic>TQ</mi>
                                       </mrow>
                                    </msub>
                                 </mrow>
                              </mfrac>
                           </mrow>
                           <mo form=postfix fence=true>)</mo>
                        </mrow>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                        <mo class=MathClass-punc>.</mo>
                        <mn>23</mn>
                     </mrow>
                  </msup>
                  <mo class=MathClass-bin>−</mo>
                  <mn>1</mn>
               </mrow>
               <mo form=postfix fence=true>]</mo>
            </mrow>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mstyle class=text>
                           <mtext>sone</mtext>
                        </mstyle>
                     </mrow>
                     <mrow>
                        <mstyle class=text>
                           <mtext>G</mtext>
                        </mstyle>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <mstyle class=text>
                     <mtext>Bark</mtext>
                  </mstyle>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(1)
</table>
<p>
   where
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>E</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>TQ</mi>
         </mrow>
      </msub>
   </math>
   denotes the excitation at threshold, and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>E</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
   </math>
   stands for excitation corresponding to reference intensity.<br class=newline>
<p>
   The total loudness (
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>N</mi>
   </math>
   ) is calculated by integrating all specific loudness values across the critical band rate
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>z</mi>
   </math>
   , as shown below [1]
<table class=equation>
   <tr>
      <td>
         <math display=block xmlns=http://www.w3.org/1998/Math/MathML class=equation>
            <mi>N</mi>
            <mo class=MathClass-rel>=</mo>
            <msubsup>
               <mrow>
                  <mo>∫</mo>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
               <mrow>
                  <mn>24</mn>
                  <mi> </mi>
                  <mstyle class=text>
                     <mtext>Bark</mtext>
                  </mstyle>
               </mrow>
            </msubsup>
            <msup>
               <mrow>
                  <mi>N</mi>
               </mrow>
               <mrow>
                  <mi>′</mi>
               </mrow>
            </msup>
            <mo class=MathClass-open>(</mo>
            <mi>z</mi>
            <mo class=MathClass-close>)</mo>
            <mi mathvariant=italic>dz</mi>
         </math>
      <td class=eq-no>(2)
</table>
<p>ISO 532-1 differs from the DIN standard primarily because the ISO standard (which is based on the algorithm mentioned above) has a more detailed specification of the individual calculation steps. These added specifications are for clarity and cause only minor differences in results. Apart from these, the Hearing Model of Sottek [7] can also be used for the calculation of loudness, refer to <a href="${linkLesson0214}" target="_blank">Lesson 2.14</a> for more details.<br class=newline>
<p>
   The table below enlists three sound sources with similar SPL but very different loudness (
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>N</mi>
   </math>
   ). It also shows the minute differences in sone values when calculated using the two different loudness calculation standards. Tools for calculating loudness are available in both open source and commercially available software solutions (e.g., Auditory Modelling Toolbox or HEAD acoustics’ ArtemiS SUITE).
   <figure class=float>
      <figcaption class=caption>Table 2.
            Comparison of average SPL, and Loudness (
            <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
               <mi>N</mi>
            </math>
            ) values computed following the DIN 45631 and ISO 532-1 standards.
      </figcaption>
      <img src=${config.ASSETS_URL}/02/07/theory/table-2.svg alt="Table 2. Comparison of average SPL, and Loudness (
      <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
         <mi>N</mi>
      </math>
      ) values computed following the DIN 45631 and ISO 532-1 standards.">
   </figure>
<p>
<h3 class=likesectionHead>Glossary</h3>
<dl>
         <dt>SPL
         <dd>Sound Pressure Level
</dl>
<h3 class=sectionHead>References</h3>
<dl class=thebibliography>
   <dt class=thebibliography id=X0-zwicker2010>[1]
   <dd class=thebibliography id=bib-1>
      <p>Eberhard Zwicker and Hugo Fastl. <span class=cmti-10>Psychoacoustics: Facts and Models</span>. 3. ed., [repr.] Springer Series in Information Sciences 22. Berlin Heidelberg: Springer, 2010. <span class=cmcsc-10><span class=small-caps>isbn</span></span>: 978-3-540-23159-2.
   <dt class=thebibliography id=X0-iso2003>[2]
   <dd class=thebibliography id=bib-2>
      <p><span class=cmti-10>ISO 226:2003 Acoustics — Normal Equal-Loudness-Level Contours</span>.
   <dt class=thebibliography id=X0-zwicker1982>[3]
   <dd class=thebibliography id=bib-3>
      <p>Eberhard Zwicker. <span class=cmti-10>Psychoakustik</span>. Berlin, Heidelberg: Springer Berlin Heidelberg, 1982. <span class=cmcsc-10><span class=small-caps>isbn</span></span>: 978-3-540-11401-7 978-3-642-68510-1. <span class=cmcsc-10><span class=small-caps>doi</span></span>: <a class=url href=https://doi.org/10.1007/978-3-642-68510-1 target="_blank"><span class=cmtt-10>10.1007/978-3-642-68510-1</span></a>.
   <dt class=thebibliography id=X0-zwicker1960>[4]
   <dd class=thebibliography id=bib-4>
      <p>Eberhard Zwicker. “Zur Unterteilung des Hörbaren Frequenzbereichs in Frequenzgruppen (Division of the Audible Frequency Range in Critical Bands)”. In: <span class=cmti-10>Acoustica </span>10.3 (1960), p. 185.
   <dt class=thebibliography id=X0-din2010>[5]
   <dd class=thebibliography id=bib-5>
      <p><span class=cmti-10>DIN 45631/A1:2010-03 Calculation of Loudness Level and Loudness </span><span class=cmti-10>from the Sound Spectrum - Zwicker Method</span>. Beuth Verlag GmbH. <span class=cmcsc-10><span class=small-caps>doi</span></span>: <a class=url href=https://doi.org/10.31030/1555185 target="_blank"><span class=cmtt-10>10.31030/1555185</span></a>.
   <dt class=thebibliography id=X0-iso2017>[6]
   <dd class=thebibliography id=bib-6>
      <p><span class=cmti-10>DIN ISO 532-1:2017 Acoustics — Methods for Calculating Loudness — </span><span class=cmti-10>Part 1: Zwicker Method</span>. Beuth Verlag GmbH. <span class=cmcsc-10><span class=small-caps>doi</span></span>: <a class=url href=https://doi.org/10.31030/3122625 target="_blank"><span class=cmtt-10>10.31030/3122625</span></a>.
   <dt class=thebibliography id=X0-sottek2005>[7]
   <dd class=thebibliography id=bib-7>
      <p>Roland Sottek and Klaus Genuit. “Models of Signal Processing in Human Hearing”. In: <span class=cmti-10>AEU - International Journal of Electronics and </span><span class=cmti-10>Communications </span>59.3 (June 2005), pp. 157–165. <span class=cmcsc-10><span class=small-caps>issn</span></span>: 14348411. <span class=cmcsc-10><span class=small-caps>doi</span></span>: <a class=url href=https://doi.org/10/fwngnm target="_blank"><span class=cmtt-10>10/fwngnm</span></a>.
</dl>`;

export default theory;
